import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useDispatch, useSelector } from "react-redux";
import { editEngagementQualification, fetchEngagementQualification, fetchSSPQuestions } from "../redux/engagementQualificationSlice";
import "./EngagementQualification.scss";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import { MotifButton, MotifFormField, MotifIcon, MotifSearch, MotifToast, MotifTooltip, MotifProgressLoader, MotifModal, MotifModalBody, MotifModalFooter } from "@ey-xd/motif-react";
import { infoIcon } from "../../../common/MotifIcons";
import checkIcon from "../../../common/images/check_icon.png";
import Avatar from "../../../common/async-avatar/avatar";
import { searchGraphUsers } from "../../../common/services/commonService";
import { QualificationDialog } from "./QualificationDialog";
import logger from "../../../common/utils/logger";

const renderYesNo = (value, onToggle, disabled, lockedToYes = false) => {
    const resolvedValue = lockedToYes ? true : value;
    const isSet = resolvedValue === true || resolvedValue === false;
    const toggleClass = isSet ? (resolvedValue ? "eq-toggle on" : "eq-toggle off") : "eq-toggle neutral";
    const wrapperClass = `${disabled ? "eq-yesno disabled" : "eq-yesno"}${lockedToYes ? " locked" : ""}`;

    return (
        <div className={wrapperClass}>
            <span className={isSet ? (resolvedValue ? "eq-no inactive" : "eq-no") : "eq-no inactive"}>No</span>
            <button
                type="button"
                className={toggleClass}
                aria-pressed={Boolean(resolvedValue)}
                aria-disabled={lockedToYes || disabled}
                onClick={lockedToYes ? undefined : onToggle}
                disabled={disabled}
            />
            <span className={isSet ? (resolvedValue ? "eq-yes" : "eq-yes inactive") : "eq-yes inactive"}>Yes</span>
        </div>
    );
};

const splitHeaderInTwoLines = (value) => {
    const words = String(value || "").replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
    if (words.length <= 1) return [words[0] || "", ""];
    if (words.length === 2) return [words[0], words[1]];

    let midIndex = 1;
    let lowestDiff = Number.MAX_SAFE_INTEGER;
    for (let index = 1; index < words.length; index += 1) {
        const firstCandidate = words.slice(0, index).join(" ");
        const secondCandidate = words.slice(index).join(" ");
        const lengthDiff = Math.abs(firstCandidate.length - secondCandidate.length);
        if (lengthDiff < lowestDiff) {
            lowestDiff = lengthDiff;
            midIndex = index;
        }
    }

    return [
        words.slice(0, midIndex).join(" "),
        words.slice(midIndex).join(" "),
    ];
};

const getQuestionColumnWidth = (value) => {
    const [firstLine, secondLine] = splitHeaderInTwoLines(value);
    const longestLineLength = Math.max(firstLine.length, secondLine.length, 1);
    const iconAndSpacingWidth = 16;
    return Math.min(420, Math.max(120, longestLineLength * 5.6 + 12 + iconAndSpacingWidth));
};

const renderQuestionInfoTooltip = (questionId, tooltipText) => {
    const formattedText = String(tooltipText || "").replace(/\s+/g, " ").trim();
    if (!formattedText) return "";

    if (Number(questionId) !== 7) {
        return <div style={{ maxWidth: 320 }}>{formattedText}</div>;
    }

    const [heading, ...items] = formattedText.split(/\s(?=\d\.\s)/);
    if (items.length === 0) {
        return <div style={{ maxWidth: 320 }}>{formattedText}</div>;
    }

    return (
        <div className="eq-question-info-tooltip">
            <div>{heading}</div>
            {items.map((item, index) => (
                <div key={`question-7-info-${index}`} className="eq-question-info-tooltip-item">
                    <span>{`${index + 1}. `}</span>
                    <span>{item.replace(/^\d\.\s*/, "")}</span>
                </div>
            ))}
        </div>
    );
};

const ToggleQuestionHeader = (props) => {
    const [firstLine, secondLine] = splitHeaderInTwoLines(props?.displayName || "");
    const tooltipText = props?.tooltipText || props?.displayName || "";
    const tooltipContent = renderQuestionInfoTooltip(props?.questionId, tooltipText);
    return (
        <div className="eq-toggle-question-header-content">
            <span className="eq-toggle-question-header-line">{firstLine}</span>
            <span className="eq-toggle-question-header-line eq-toggle-question-header-line--with-icon">
                <span>{secondLine}</span>
                <MotifTooltip
                    allowClick
                    allowHover
                    closeButtonProps={{ style: { display: "none" } }}
                    flip
                    id={`toggle-question-tooltip-${props?.column?.getColId?.() || "question"}`}
                    placement="auto"
                    trigger={<MotifIcon src={infoIcon} />}
                    variant="alt"
                >
                    {tooltipContent}
                </MotifTooltip>
            </span>
        </div>
    );
};

const renderDecision = (value) => {
    const normalized = String(value || "").toLowerCase().trim();
    const displayValue = normalized === "in progress" ? "In progress" : (value || "-");
    return (
        <span className={`eq-status ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`}>
            {displayValue}
        </span>
    );
};

const isQualifiedDecision = (value) => String(value || "").toLowerCase().trim() === "qualified";

const getStatusKey = (value) => {
    const key = String(value || "").toLowerCase().trim();
    if (key === "pending" || key === "is pending") return "pending";
    if (key === "in progress") return "in progress";
    if (key === "completed" || key === "not qualified") return "completed";
    return "completed";
};

const getRowKey = (row) => `${row?.engagementId_GUID}-${row?.engagementId}`;

const setFilterParams = {
    textFormatter: (value) => String(value || "").toLowerCase().trim(),
};
const MAIN_PAGE = "main_page";
const COMPLETE_QUALIFICATION_PAGE = "complete_qualification_page";

const normalizeQuestionId = (value, fallback = null) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
    return fallback;
};

const normalizeQuestion = (item, index = 0) => {
    if (typeof item === "string") {
        const question = String(item || "").replace(/\s+/g, " ").trim();
        if (!question) return null;
        return {
            questionId: index + 1,
            question,
            page: COMPLETE_QUALIFICATION_PAGE,
            questionInfo: question,
        };
    }

    if (!item || typeof item !== "object") return null;

    const question = String(item?.question || "").replace(/\s+/g, " ").trim();
    if (!question) return null;

    return {
        questionId: normalizeQuestionId(item?.questionId, index + 1),
        question,
        page: String(item?.page || "").toLowerCase().trim() || COMPLETE_QUALIFICATION_PAGE,
        questionInfo: String(item?.questionInfo || "").replace(/\s+/g, " ").trim() || question,
    };
};

const normalizeQuestionAnswers = (questionAnswers = []) => {
    if (!Array.isArray(questionAnswers)) return [];
    return questionAnswers
        .map((item, index) => ({
            questionId: normalizeQuestionId(item?.questionId, index + 1),
            answer: item?.answer,
            engagementIdGuid: item?.engagementIdGuid || item?.engagementId_GUID || item?.engagementIdGUID || "",
        }))
        .filter((item) => item.questionId);
};

const getQuestionAnswerById = (questionAnswers = [], questionId) => {
    const normalizedId = normalizeQuestionId(questionId, null);
    if (!normalizedId) return null;
    const match = questionAnswers.find((item) => normalizeQuestionId(item?.questionId, null) === normalizedId);
    const answer = match?.answer;
    if (answer === true || answer === false) return answer;
    return null;
};

const setQuestionAnswerById = (questionAnswers = [], questionId, answer, engagementIdGuid = "") => {
    const normalizedId = normalizeQuestionId(questionId, null);
    if (!normalizedId) return normalizeQuestionAnswers(questionAnswers);

    const normalizedAnswers = normalizeQuestionAnswers(questionAnswers);
    const existingIndex = normalizedAnswers.findIndex((item) => normalizeQuestionId(item?.questionId, null) === normalizedId);
    const nextAnswer = {
        questionId: normalizedId,
        answer,
        engagementIdGuid,
    };

    if (existingIndex >= 0) {
        normalizedAnswers[existingIndex] = {
            ...normalizedAnswers[existingIndex],
            ...nextAnswer,
        };
        return normalizedAnswers;
    }

    return [...normalizedAnswers, nextAnswer];
};

export const EngagementQualification = ({ isEngagementAdmin = "" }) => {
    const [gridApi, setGridApi] = useState(null);
    const [activeStatus, setActiveStatus] = useState("in progress");
    const [searchText, setSearchText] = useState("");
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);
    const [warningDialogType, setWarningDialogType] = useState("servicesEnded");
    const [qualificationDialogOpen, setQualificationDialogOpen] = useState(false);
    const [selectedEngagementData, setSelectedEngagementData] = useState(null);
    const dispatch = useDispatch();
    const engagementQualification = useSelector(
        (state) => state?.engagementQualification?.data
    );
    const engagementQualificationStatus = useSelector(
        (state) => state?.engagementQualification?.status
    );
    const sspQuestions = useSelector(
        (state) => state?.engagementQualification?.sspQuestions || []
    );
    const sspQuestionsStatus = useSelector(
        (state) => state?.engagementQualification?.sspQuestionsStatus || "idle"
    );

    const [rowData, setRowData] = useState([]);
    const [sessionTabStatusMap, setSessionTabStatusMap] = useState({});
    const [hasAutoSelectedStatus, setHasAutoSelectedStatus] = useState(false);
    const [partnerGraphDataByEmail, setPartnerGraphDataByEmail] = useState({});
    const [partnerGraphLoadingByEmail, setPartnerGraphLoadingByEmail] = useState({});
    const [warningSaveRow, setWarningSaveRow] = useState(null);
    const [warningOriginalRow, setWarningOriginalRow] = useState(null);
    const [warningSaveLoading, setWarningSaveLoading] = useState(false);
    const [qualifiedToastState, setQualifiedToastState] = useState({ visible: false, engagementId: "" });
    const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState(false);

    const normalizedQuestions = useMemo(() => {
        if (!Array.isArray(sspQuestions)) return [];
        return sspQuestions.map((item, index) => normalizeQuestion(item, index)).filter(Boolean);
    }, [sspQuestions]);

    const mainPageQuestions = useMemo(() => {
        return normalizedQuestions
            .filter((question) => question.page === MAIN_PAGE)
            .sort((first, second) => (first?.questionId || 0) - (second?.questionId || 0));
    }, [normalizedQuestions]);

    const isServicesEndedQuestion = useCallback((questionId) => {
        const question = mainPageQuestions.find((item) => item?.questionId === questionId);
        return String(question?.question || "").toLowerCase().includes("engagement services ended");
    }, [mainPageQuestions]);

    const buildEditPayload = useCallback((row) => {
        const mappedQuestionAnswers = normalizeQuestionAnswers(row?.questionAnswers)
            .filter((item) => item.answer === true || item.answer === false)
            .map((item) => ({
                questionId: item.questionId,
                answer: item.answer,
                engagementIdGuid: row?.engagementId_GUID || "",
            }));

        return {
            engagementId_GUID: row?.engagementId_GUID || "",
            questionAnswers: mappedQuestionAnswers,
            finalDecision: row?.finalDecision || "Pending",
        };
    }, []);

    const saveQualificationEdit = useCallback(async (row, isDialogSave = false) => {
        if (!row?.engagementId_GUID) return;

        if (isDialogSave) setWarningSaveLoading(true);
        try {
            await dispatch(
                editEngagementQualification({
                    area: row?.area,
                    payload: buildEditPayload(row),
                })
            ).unwrap();
            if (isDialogSave) {
                setWarningDialogOpen(false);
                setWarningSaveRow(null);
                setWarningOriginalRow(null);
            }
        } catch (error) {
            logger.error("Failed to save engagement qualification edit:", error);
            setApiErrorDialogOpen(true);
        } finally {
            if (isDialogSave) setWarningSaveLoading(false);
        }
    }, [buildEditPayload, dispatch]);

    useEffect(() => {
        if (engagementQualificationStatus === "idle" || !engagementQualificationStatus) {
            dispatch(fetchEngagementQualification({ isEngagementAdmin }));
        }
    }, [dispatch, engagementQualificationStatus, isEngagementAdmin]);

    useEffect(() => {
        if (sspQuestionsStatus === "idle" || !sspQuestionsStatus) {
            dispatch(fetchSSPQuestions());
        }
    }, [dispatch, sspQuestionsStatus]);

    useEffect(() => {
        if (!Array.isArray(engagementQualification)) return;

        setRowData(engagementQualification);
        setSessionTabStatusMap((prev) => {
            const next = { ...prev };
            let changed = false;
            engagementQualification.forEach((row) => {
                const key = getRowKey(row);
                if (!next[key]) {
                    next[key] = getStatusKey(row.finalDecision);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });

        if (!hasAutoSelectedStatus && engagementQualification.length > 0) {
            const counts = { "in progress": 0, pending: 0, completed: 0 };
            engagementQualification.forEach((row) => {
                const status = getStatusKey(row.finalDecision);
                counts[status] += 1;
            });

            if (counts["in progress"] > 0) {
                setActiveStatus("in progress");
            } else if (counts.pending > 0) {
                setActiveStatus("pending");
            } else if (counts.completed > 0) {
                setActiveStatus("completed");
            }
            setHasAutoSelectedStatus(true);
        }

        if (engagementQualification.length === 0) {
            setHasAutoSelectedStatus(false);
        }
    }, [engagementQualification, hasAutoSelectedStatus]);

    const handleToggle = useCallback((rowKey, questionId) => {
        let nextWarningSaveRow = null;
        let nextWarningOriginalRow = null;
        let immediateSaveRow = null;

        setRowData((prev) =>
            prev.map((row) => {
                const key = `${row.engagementId_GUID}-${row.engagementId}`;
                if (key !== rowKey) return row;

                const currentAnswer = getQuestionAnswerById(row?.questionAnswers || [], questionId);
                const nextValue = currentAnswer === true ? false : true;
                const nextQuestionAnswers = setQuestionAnswerById(
                    row?.questionAnswers || [],
                    questionId,
                    nextValue,
                    row?.engagementId_GUID || ""
                );

                if (currentAnswer === true && nextValue === false) {
                    const servicesEnded = isServicesEndedQuestion(questionId);
                    setWarningDialogType(servicesEnded ? "servicesEnded" : "notQualified");
                    setWarningDialogOpen(true);
                    const updatedRow = {
                        ...row,
                        questionAnswers: nextQuestionAnswers,
                        finalDecision: servicesEnded ? "Pending" : "Not Qualified",
                    };
                    nextWarningSaveRow = updatedRow;
                    nextWarningOriginalRow = row;
                    return updatedRow;
                }

                const updatedRow = { ...row, questionAnswers: nextQuestionAnswers };
                const allYes = mainPageQuestions.length > 0 && mainPageQuestions.every(
                    (question) => getQuestionAnswerById(nextQuestionAnswers, question.questionId) === true
                );

                if (allYes) {
                    const allYesRow = { ...updatedRow, finalDecision: "In progress" };
                    immediateSaveRow = allYesRow;
                    return allYesRow;
                }

                immediateSaveRow = updatedRow;

                return updatedRow;
            })
        );
        if (nextWarningSaveRow) {
            setWarningSaveRow(nextWarningSaveRow);
            setWarningOriginalRow(nextWarningOriginalRow);
            return;
        }
        if (immediateSaveRow) {
            saveQualificationEdit(immediateSaveRow, false);
        }
    }, [isServicesEndedQuestion, mainPageQuestions, saveQualificationEdit]);

    const handleWarningDialogClose = useCallback(() => {
        if (warningSaveLoading) return;
        if (warningOriginalRow) {
            const warningKey = `${warningOriginalRow?.engagementId_GUID}-${warningOriginalRow?.engagementId}`;
            setRowData((prev) =>
                prev.map((row) =>
                    `${row?.engagementId_GUID}-${row?.engagementId}` === warningKey ? warningOriginalRow : row
                )
            );
        }
        setWarningDialogOpen(false);
        setWarningSaveRow(null);
        setWarningOriginalRow(null);
    }, [warningOriginalRow, warningSaveLoading]);

    const handleWarningDialogOk = useCallback(async () => {
        if (!warningSaveRow || warningSaveLoading) {
            setWarningDialogOpen(false);
            setWarningOriginalRow(null);
            return;
        }
        await saveQualificationEdit(warningSaveRow, true);
    }, [saveQualificationEdit, warningSaveLoading, warningSaveRow]);

    const handleQualifiedToastClose = useCallback(() => {
        setQualifiedToastState((prev) => ({ ...prev, visible: false }));
    }, []);
    const handleApiErrorDialogClose = useCallback(() => {
        setApiErrorDialogOpen(false);
    }, []);

    const handleQualificationSubmitSuccess = useCallback((payload = {}) => {
        const engagementId = payload?.engagementId || selectedEngagementData?.engagementId || "";
        setQualifiedToastState({
            visible: true,
            engagementId,
        });
    }, [selectedEngagementData]);

    useEffect(() => {
        if (!qualifiedToastState.visible) return;
        const timeoutId = setTimeout(() => {
            setQualifiedToastState((prev) => ({ ...prev, visible: false }));
        }, 5000);
        return () => clearTimeout(timeoutId);
    }, [qualifiedToastState.visible]);
    const isRowDisabled = useCallback((row) => {
        if (!Array.isArray(mainPageQuestions) || mainPageQuestions.length === 0) return false;
        return mainPageQuestions.some((question) =>
            getQuestionAnswerById(row?.questionAnswers || [], question.questionId) === false
        );
    }, [mainPageQuestions]);

    const getPartnerEmail = useCallback((row) => {
        const data = row || {};
        const directEmail =
            data?.epEmail ||
            data?.engagementPartnerEmail ||
            data?.partnerEmail ||
            data?.engagementPartnerMail ||
            "";

        if (directEmail) return String(directEmail).trim().toLowerCase();

        const maybeEmail =
            data?.engagementPartner ||
            data?.partnerName ||
            data?.epName ||
            "";
        if (String(maybeEmail).includes("@")) {
            return String(maybeEmail).trim().toLowerCase();
        }

        return "";
    }, []);

    const fetchPartnerGraphData = useCallback(async (row) => {
        const email = getPartnerEmail(row);
        if (!email || partnerGraphDataByEmail[email] || partnerGraphLoadingByEmail[email]) return;

        setPartnerGraphLoadingByEmail((prev) => ({ ...prev, [email]: true }));
        try {
            const results = await searchGraphUsers(email);
            const user =
                results?.find((item) => String(item?.status || "").toLowerCase() === email) ||
                results?.[0] ||
                null;

            setPartnerGraphDataByEmail((prev) => ({
                ...prev,
                [email]: {
                    displayName: user?.displayName || "",
                    profilePhoto: user?.image || null,
                },
            }));
        } catch (error) {
            setPartnerGraphDataByEmail((prev) => ({ ...prev, [email]: null }));
        } finally {
            setPartnerGraphLoadingByEmail((prev) => {
                const next = { ...prev };
                delete next[email];
                return next;
            });
        }
    }, [getPartnerEmail, partnerGraphDataByEmail, partnerGraphLoadingByEmail]);

    const renderEngagementInfoContent = useCallback((row) => {
        const isNotApplicableValue = (value) =>
            String(value || "").trim().toLowerCase() === "not applicable";

        const renderInfoRow = (label, value, isStacked = false) => {
            if (isNotApplicableValue(value)) return null;
            return (
                <div className={`eq-epinfo-row${isStacked ? " eq-epinfo-row--stacked" : ""}`}>
                    <span className="eq-epinfo-label">{label}</span>
                    <span className="eq-epinfo-value">{value || "-"}</span>
                </div>
            );
        };

        const data = row || {};
        const partnerEmail = getPartnerEmail(row);
        const graphData = partnerEmail ? partnerGraphDataByEmail[partnerEmail] : null;
        const defaultPartnerName = data?.partnerName || data?.engagementPartner || data?.epName || data?.epEmail || "-";
        const resolvedPartnerName = graphData?.displayName || defaultPartnerName;
        const partnerName = isNotApplicableValue(resolvedPartnerName) ? "-" : resolvedPartnerName;
        const engagementIdValue = row?.engagementId || "-";
        const showEngagementId = !isNotApplicableValue(engagementIdValue);
        const engagementManagerValue = data.engagementManager || data.emName || data.engagementManagerEmail || "-";

        return (
            <div className="eq-epinfo-card">
                <div className="eq-epinfo-top">
                    <div className="eq-epinfo-partner">
                        <div className="eq-epinfo-partner-avatar">
                            <Avatar
                                src={graphData?.profilePhoto || ""}
                                userName={partnerName}
                                size="large"
                            />
                        </div>
                        <div className="eq-epinfo-partner-meta">
                            <div className="eq-epinfo-partner-name">{partnerName}</div>
                            <div className="eq-epinfo-partner-role">Engagement Partner</div>
                        </div>
                    </div>
                    {showEngagementId ? (
                        <div className="eq-epinfo-id">
                            <div className="eq-epinfo-id-label">Engagement ID</div>
                            <div className="eq-epinfo-id-value">{engagementIdValue}</div>
                        </div>
                    ) : null}
                </div>

                {renderInfoRow("Engagement Name", row?.engagementName || "-", true)}
                {renderInfoRow("Engagement Manager", engagementManagerValue)}
                {renderInfoRow("SSL", data.ssl || "-")}
                {renderInfoRow(
                    "SORT - Service Name",
                    (() => {
                        const sortServiceName = String(data?.sortServiceName || "").trim();
                        const serviceName = String(data?.serviceName || "").trim();

                        if (serviceName) {
                            return [sortServiceName, serviceName].filter(Boolean).join(" - ");
                        }

                        return sortServiceName || "-";
                    })()
                )}
                {renderInfoRow("Target Entity", data.targetEntity || "-")}
                {renderInfoRow("Date Mercury Code Opened", data.dataMercuryCodeOpened?.split(" ")[0] || "-")}
                {renderInfoRow("Client Information Clause present", data.clientInfoClausePresent || "-")}
                {renderInfoRow("Target Information Clause Found", data.targetInfoClauseFound || "-")}
                {renderInfoRow("Limitations on Liability found", data.limitationsOnLiabilityFound || "-")}
            </div>
        );
    }, [getPartnerEmail, partnerGraphDataByEmail]);

    const columnDefs = useMemo(
        () => [
            {
                headerName: "Engagement Name",
                field: "engagementName",
                pinned: "left",
                lockPinned: true,
                minWidth: 155,
                filter: true,
                getQuickFilterText: (params) => String(params?.value || ""),
                cellRenderer: (params) => {
                    const row = params.data || {};
                    const name = params.value || "-";
                    const isEyDataOnly = row?.isClientData === false || String(row?.isClientData).toLowerCase() === "false";

                    return (
                        <div className="eq-engagement-name-cell">
                            <div className="eq-engagement-name-content">
                                <span className="eq-engagement-name-text" title={name}>{name}</span>
                                {isEyDataOnly ? (
                                    <span className="eq-ey-data-only-label" title="Only upload EY data; do not include client data.">
                                        EY Data only
                                    </span>
                                ) : null}
                            </div>
                            <Tooltip
                                classes={{ popper: "eq-epinfo-tooltip-popper", tooltip: "eq-epinfo-tooltip" }}
                                placement="auto-start"
                                PopperProps={{
                                    disablePortal: false,
                                    popperOptions: {
                                        strategy: "fixed",
                                        modifiers: [
                                            {
                                                name: "preventOverflow",
                                                options: {
                                                    rootBoundary: "viewport",
                                                    altAxis: true,
                                                    padding: 8,
                                                },
                                            },
                                            {
                                                name: "flip",
                                                options: {
                                                    fallbackPlacements: ["left-start", "bottom-start", "top-start"],
                                                },
                                            },
                                        ],
                                    },
                                }}
                                title={renderEngagementInfoContent(row)}
                                arrow={false}
                                enterDelay={100}
                            >
                                <button
                                    type="button"
                                    className="eq-info-trigger"
                                    aria-label={`Show engagement details for ${name}`}
                                    onMouseEnter={() => fetchPartnerGraphData(row)}
                                    onFocus={() => fetchPartnerGraphData(row)}
                                >
                                    <img src={infoIcon} alt="" />
                                </button>
                            </Tooltip>
                        </div>
                    );
                },
            },
            { headerName: "Engagement ID", field: "engagementId", minWidth: 128, width: 132, maxWidth: 138, flex: 0.72, wrapHeaderText: false, headerClass: "eq-id-client-header eq-engagement-id-header", cellClass: "eq-id-client-cell eq-engagement-id-cell", filter: "agSetColumnFilter", filterParams: setFilterParams, getQuickFilterText: (params) => String(params?.value || "") },
            {
                headerName: "Client Name",
                field: "clientName",
                minWidth: 112,
                width: 118,
                maxWidth: 140,
                flex: 0.85,
                wrapHeaderText: false,
                headerClass: "eq-id-client-header",
                cellClass: "eq-id-client-cell",
                filter: "agSetColumnFilter",
                filterParams: setFilterParams,
                getQuickFilterText: (params) => String(params?.value || ""),
                cellRenderer: (params) => {
                    const value = params?.value || "-";
                    return <span className="eq-client-name-ellipsis" title={value}>{value}</span>;
                },
            },
            ...mainPageQuestions.map((question) => ({
                headerName: question?.question || `Question ${question?.questionId}`,
                field: `question_${question?.questionId}`,
                headerClass: "eq-toggle-question-header",
                headerComponent: ToggleQuestionHeader,
                headerComponentParams: {
                    questionId: question?.questionId,
                    displayName: question?.question || `Question ${question?.questionId}`,
                    tooltipText: question?.questionInfo || question?.question || `Question ${question?.questionId}`,
                },
                width: getQuestionColumnWidth(question?.question || ""),
                minWidth: 130,
                suppressSizeToFit: true,
                cellClass: "eq-toggle-question-cell",
                cellRenderer: (params) => {
                    const row = params.data || {};
                    const currentValue = getQuestionAnswerById(row?.questionAnswers || [], question?.questionId);
                    if (isQualifiedDecision(row.finalDecision)) {
                        return renderYesNo(true, undefined, false, true);
                    }

                    return renderYesNo(
                        currentValue,
                        () => {
                            const rowDisabled = isRowDisabled(row);
                            if (rowDisabled && currentValue !== false) return;
                            handleToggle(`${row.engagementId_GUID}-${row.engagementId}`, question?.questionId);
                        },
                        isRowDisabled(params.data) && currentValue !== false
                    );
                },
            })),
            {
                headerName: "Final decision for reuse",
                field: "finalDecision",
                minWidth: 120,
                width: 120,
                maxWidth: 220,
                pinned: "right",
                lockPinned:true,
                suppressSizeToFit: true,
                wrapHeaderText: true,
                cellClass: "eq-final-decision-cell",
                cellRenderer: (params) => renderDecision(params.value),
            },
            {
                headerName: "Actions",
                field: "actions",
                pinned: "right",
                lockPinned: true,
                minWidth: 165,
                width: 176,
                maxWidth: 176,
                cellRenderer: (params) => {
                    const row = params.data || {};
                    const isActionDisabled = isRowDisabled(row);
                    if (isQualifiedDecision(row.finalDecision)) {
                        return (
                            <span className="eq-action-completed">
                                <span className="eq-action-completed-icon">
                                    <img src={checkIcon} alt="" />
                                </span>
                                <span>Qualification Completed</span>
                            </span>
                        );
                    }

                    return (
                        <button
                            className={`eq-action ${isActionDisabled ? "eq-action-disabled" : ""}`}
                            type="button"
                            disabled={isActionDisabled}
                            aria-disabled={isActionDisabled}
                            onClick={() => {
                                setSelectedEngagementData(row);
                                setQualificationDialogOpen(true);
                            }}
                        >
                            Complete Qualification
                        </button>
                    );
                },
            },
        ],
        [fetchPartnerGraphData, handleToggle, isRowDisabled, mainPageQuestions, renderEngagementInfoContent]
    );

    const defaultColDef = useMemo(
        () => ({
            sortable: true,
            resizable: true,
            filter: false,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            getQuickFilterText: () => "",
        }),
        []
    );

    const statusCounts = useMemo(() => {
        const counts = { "in progress": 0, pending: 0, completed: 0 };
        rowData.forEach((row) => {
            const rowKey = getRowKey(row);
            const tabStatus = sessionTabStatusMap[rowKey] || getStatusKey(row.finalDecision);
            counts[tabStatus] += 1;
        });
        return counts;
    }, [rowData, sessionTabStatusMap]);

    const normalizeStatus = useCallback((value) => {
        return getStatusKey(value);
    }, []);

    const filteredRows = useMemo(() => {
        return rowData.filter((row) => {
            const rowKey = getRowKey(row);
            const tabStatus = sessionTabStatusMap[rowKey] || normalizeStatus(row.finalDecision);
            return tabStatus === activeStatus;
        });
    }, [rowData, activeStatus, normalizeStatus, sessionTabStatusMap]);

    const handleSearchChange = (event) => {
        const value = event.target.value || "";
        setSearchText(value);
        if (gridApi?.setGridOption) {
            gridApi.setGridOption("quickFilterText", value);
        } else {
            gridApi?.setQuickFilter?.(value);
        }
    };

    useEffect(() => {
        if (!gridApi) return;
        if (gridApi?.setGridOption) {
            gridApi.setGridOption("quickFilterText", searchText || "");
        } else {
            gridApi?.setQuickFilter?.(searchText || "");
        }
    }, [gridApi, searchText, filteredRows]);

    useEffect(() => {
        if (!gridApi) return;
        const handleResize = () => gridApi.sizeColumnsToFit();
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [gridApi]);

    useEffect(() => {
        if (!gridApi) return;
        gridApi.sizeColumnsToFit();
    }, [activeStatus, gridApi]);

    return (
        <div className="engagement-qualification">
            {qualifiedToastState.visible ? (
                <MotifToast
                    variant="success"
                    className="eq-qualified-success-toast"
                    onClose={handleQualifiedToastClose}
                >
                    <div className="eq-qualified-toast-content">
                        <div className="eq-qualified-toast-engagement-id">Qualification Completed successfully for</div>
                        <div className="eq-qualified-toast-engagement-id">Engagement ID {qualifiedToastState.engagementId || "."}</div>
                        <div className="eq-qualified-toast-message">Engagement qualification has been completed now you can proceed with data upload.</div>
                    </div>
                </MotifToast>
            ) : null}
            <Dialog
                open={warningDialogOpen}
                onClose={handleWarningDialogClose}
                aria-labelledby="engagement-qualification-warning"
                className="eq-warning-dialog"
            >
                <DialogTitle id="engagement-qualification-warning">
                    <IconButton
                        onClick={handleWarningDialogClose}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                        disabled={warningSaveLoading}
                    >
                        <CloseIcon />
                    </IconButton>
                    <img src={require("../../../common/images/warning-triangle.png")} alt="Warning" />
                </DialogTitle>
                <DialogContent>
                    {warningDialogType === "servicesEnded" ? (
                        <>
                            <strong>Engagement Qualification can not be completed.</strong>
                            <p>This engagement is not eligible for EY-P DBS. Only closed engagements are allowed so marking as pending. Please return after closure.</p>
                        </>
                    ) : (
                        <>
                            <strong>Engagement qualification cannot be completed.</strong>
                            <p>The engagement does not qualify for EY-P DBS reuse.
                                <br />
                                No further action required.</p>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <MotifButton
                        onClick={handleWarningDialogOk}
                        size="medium"
                        type="button"
                        disabled={warningSaveLoading}
                    >
                        OK
                    </MotifButton>
                </DialogActions>
            </Dialog>
            <QualificationDialog
                open={qualificationDialogOpen}
                onClose={() => setQualificationDialogOpen(false)}
                engagementData={selectedEngagementData}
                onSubmitSuccess={handleQualificationSubmitSuccess}
               
            />
            <MotifModal
                show={apiErrorDialogOpen}
                onClose={handleApiErrorDialogClose}
                size="sm"
                className="eq-api-error-dialog"
            >
                <MotifModalBody>
                    <p>something went wrong, please try again</p>
                </MotifModalBody>
                <MotifModalFooter>
                    <MotifButton
                        onClick={handleApiErrorDialogClose}
                        size="medium"
                        type="button"
                    >
                        OK
                    </MotifButton>
                </MotifModalFooter>
            </MotifModal>
            <div className="eq-header">
                 <div>
                    <h4></h4>
                    <p></p>
                </div>
                <div className="eq-header-actions">
                    <div className="eq-search header-input-partner">
                        <MotifFormField>
                            <MotifSearch
                                aria-label="Search"
                                onChange={handleSearchChange}
                                placeholder="Search ..."
                                value={searchText}
                            />
                        </MotifFormField>
                    </div>
                    <div className="eq-counts">
                        <button
                            type="button"
                            className={`status-tab in-progress ${activeStatus === "in progress" ? "active" : ""}`}
                            onClick={() => setActiveStatus("in progress")}
                        >
                            In progress <strong>{statusCounts["in progress"]}</strong>
                        </button>
                        <button
                            type="button"
                            className={`status-tab pending ${activeStatus === "pending" ? "active" : ""}`}
                            onClick={() => setActiveStatus("pending")}
                        >
                            Pending <strong>{statusCounts.pending}</strong>
                        </button>
                        <button
                            type="button"
                            className={`status-tab completed ${activeStatus === "completed" ? "active" : ""}`}
                            onClick={() => setActiveStatus("completed")}
                        >
                            Completed <strong>{statusCounts.completed}</strong>
                        </button>
                    </div>
                </div>
            </div>

            <div className="eq-grid ag-theme-quartz">
                {engagementQualificationStatus === "loading" ? (
                    <div className="eq-loader">
                        <MotifProgressLoader show variant="default" />
                    </div>
                ) : (
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={filteredRows}
                        defaultColDef={defaultColDef}
                        floatingFilter={true}
                        rowHeight={42}
                        alwaysShowVerticalScroll={true}
                        suppressMenuHide={true}
                        getContextMenuItems={() => ["copy"]}
                        onGridReady={(params) => {
                            setGridApi(params.api);
                            params.api.sizeColumnsToFit();
                        }}
                    />
                )}
            </div>
        </div>
    );
};
